import React, { useState, useEffect, useCallback, useRef } from 'react'
import { useIsFeatureEnabled } from '@/contexts/AnalyticsProvider'
import SharedModal from '@/components/Modal/SharedModal'
import {
  useIsVersionDismissed,
  useDismissVersionNotification,
  useVersionNotificationTracking,
} from '@/queries/hooks/use-version-notification'
import { RELEASED_CHANGELOG_TRIGGER_ID } from '@/containers/MapControls/Released/released-constants'

const CURRENT_VERSION = 'v5.4.0'
const MODAL_DELAY_MS = 1000

interface VersionNotificationModalProps {
  /** Whether the user is logged in */
  isLoggedIn: boolean
  /** User UUID for tracking dismissal per user */
  userUUID?: string
}

interface VersionNotificationVisibilityOptions {
  isLoggedIn: boolean
  userUUID?: string
  showModalOnLaunch?: boolean
  isDismissed: boolean
  isCheckingDismissal: boolean
  trackModalShown: (version: string) => void
}

const useVersionNotificationVisibility = ({
  isLoggedIn,
  userUUID,
  showModalOnLaunch,
  isDismissed,
  isCheckingDismissal,
  trackModalShown,
}: VersionNotificationVisibilityOptions) => {
  const [isModalOpen, setIsModalOpen] = useState(false)
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  useEffect(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current)
      timerRef.current = null
    }

    if (isCheckingDismissal) return

    if (isLoggedIn && userUUID && showModalOnLaunch && !isDismissed) {
      timerRef.current = setTimeout(() => {
        setIsModalOpen(true)
        trackModalShown(CURRENT_VERSION)
      }, MODAL_DELAY_MS)
    }

    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current)
        timerRef.current = null
      }
    }
  }, [
    isLoggedIn,
    userUUID,
    showModalOnLaunch,
    isDismissed,
    isCheckingDismissal,
    trackModalShown,
  ])

  return { isModalOpen, setIsModalOpen }
}

const VersionNotificationModal: React.FC<VersionNotificationModalProps> = ({
  isLoggedIn,
  userUUID,
}) => {
  const showModalOnLaunch = useIsFeatureEnabled('show_modal_on_launch')

  // React Query hooks for version notification management
  const { isDismissed, isLoading: isCheckingDismissal } = useIsVersionDismissed(
    userUUID,
    CURRENT_VERSION
  )
  const dismissVersionMutation = useDismissVersionNotification()
  const { trackModalShown } = useVersionNotificationTracking()
  const { isModalOpen, setIsModalOpen } = useVersionNotificationVisibility({
    isLoggedIn,
    userUUID,
    showModalOnLaunch,
    isDismissed,
    isCheckingDismissal,
    trackModalShown,
  })

  const handleDismiss = useCallback((): void => {
    if (!userUUID) return

    setIsModalOpen(false)

    // Use React Query mutation to save dismissal (tracking is handled in the hook)
    dismissVersionMutation.mutate({
      userUUID,
      version: CURRENT_VERSION,
    })
  }, [userUUID, dismissVersionMutation, setIsModalOpen])

  const handleViewReleaseNotes = useCallback((): void => {
    const releaseNotesTrigger = document.getElementById(
      RELEASED_CHANGELOG_TRIGGER_ID
    )

    if (!releaseNotesTrigger) return

    handleDismiss()
    releaseNotesTrigger.click()
  }, [handleDismiss])

  if (!isModalOpen) return null

  return (
    <SharedModal
      title={`${CURRENT_VERSION} - More powerful map editing`}
      isOpen={isModalOpen}
      onClose={handleDismiss}
      backgroundClickDismiss={false}
      footerActionButtons={[
        {
          label: 'View full release notes',
          onClick: handleViewReleaseNotes,
          variant: 'secondary',
          disabled: dismissVersionMutation.isPending,
        },
        {
          label: dismissVersionMutation.isPending
            ? 'Saving...'
            : 'Start exploring',
          onClick: handleDismiss,
          variant: 'primary',
          disabled: dismissVersionMutation.isPending,
        },
      ]}
      size='md'
      scrollBody
    >
      <div className='space-y-5 text-[rgba(255,255,255,0.8)] leading-relaxed'>
        <p>
          Edit images, draw shapes, and refine feature details without breaking
          your flow.
        </p>

        <ul className='list-disc space-y-3 pl-5'>
          <li>
            <strong className='text-white'>
              Edit images without starting over
            </strong>{' '}
            - Crop, replace, rotate, align, or fade imported images directly on
            the map.
          </li>
          <li>
            <strong className='text-white'>Draw and edit in place</strong> -
            Create five shape types, undo changes, and save drawings into
            layers.
          </li>
          <li>
            <strong className='text-white'>
              Navigate busy maps confidently
            </strong>{' '}
            - Cycle overlapping features and work with clearer tabs, details,
            and empty states.
          </li>
          <li>
            <strong className='text-white'>Copy layers with confidence</strong>{' '}
            - Reuse layer setup and attributes while protecting unsaved work.
          </li>
        </ul>

        <div className='rounded-lg border border-accent/30 bg-accent/10 px-4 py-3'>
          <p className='font-semibold text-white'>Pro tip</p>
          <p className='text-sm'>
            Double-click any imported image to reopen its editing tools.
          </p>
        </div>

        <p className='text-sm'>
          Questions? Open the chat widget in the bottom-right corner of the map.
        </p>
      </div>
    </SharedModal>
  )
}

export default VersionNotificationModal
